import * as React from 'react';
import { useNavigate, useParams } from 'react-router';
import DataApi, { TPaymentType, TProductType } from '../../api/DataApi';
import VignetApi from '../../api/VignetApi';
import AgreementBox from '../../components/agreement-box/AgreementBox';
import CenteredForm from '../../components/centered-form/CenteredForm';
import EditHeader from '../../components/edit-header/EditHeader';
import KeyValuePair from '../../components/key-value-pair/KeyValuePair';
import LabeledInputField from '../../components/labeled-input-field/LabeledInputField';
import PageHeader from '../../components/page-header/PageHeader';
import FormStepper, { TStep } from '../form-stepper/FormStepper';
import TitleLineComponent from '../../components/page-layout/TitleLineComponent';
import PaymentTypeSelector from '../../components/payment-type-selector/PaymentTypeSelector';
import AlertBox, { ESeverity } from '../../components/alert-box/AlertBox';
import style from './MooringPage.module.css';

// --- TYPES ---
type TFormDataEntry = {
  value: string;
  validator?: Function;
};

type TFormData = {
  [key: string]: TFormDataEntry;
};

// --- STEP COMPONENTS ---

const Step0VignetNumber = ({
  formData,
  pageError,
}: {
  formData: TFormData;
  pageError?: string;
}) => (
  <>
    <div id={style.header}>Nummer van uw vignet</div>
    <div id={style.subHeader}>
      U kunt hier '6 uur afmeren' of '24 uur afmeren' kopen. In Amsterdam geldt het afmeerproduct
      alleen voor boten korter dan 10 meter. In Weesp mag u met een boot van meer dan 10 meter
      afmeren met een afmeerproduct.
      <br />
      <br />
      <strong>Let op!</strong> Het afmeerproduct is na aankoop direct actief.
    </div>
    <LabeledInputField text={'Nummer van uw vignet'} name={'vignetNumber'} formData={formData} />
    <LabeledInputField
      text={'Herhaal nummer van uw vignet'}
      name={'repeatVignetNumber'}
      formData={formData}
    />
    <div id={style.pageError}>{pageError}</div>
    <img id={style.exampleVignet} src="../vignetnummber.png" alt="Example Vignet Number" />
    <div id={style.disclaimer}>
      De gemeente Amsterdam gaat zorgvuldig en veilig met uw persoonsgegevens om. Wij houden ons
      daarbij aan de privacywetten en -regels. Dat betekent bijvoorbeeld dat wij uw gegevens alleen
      voor deze aanvraag gebruiken en niet voor iets anders. Meer informatie vindt u in de{' '}
      <a
        href="https://www.amsterdam.nl/privacy/specifieke/privacyverklaring-parkeren-verkeer-bouw/verkeersmanagement-gracht-privacy/"
        target="_blank"
        rel="noreferrer"
      >
        privacyverklaring Binnenhavengeld
      </a>
      .
    </div>
  </>
);

const Step1MooringType = ({ vignetTypes, mooringType, handleOnChange, pageError }: any) => (
  <>
    <div id={style.header}>Maak uw keuze</div>
    <>
      {vignetTypes ? (
        vignetTypes.map((vignetType: TProductType, index: number) => (
          <div key={'vignet_type_option_' + index} className={style.selectTypeContainer}>
            <input
              defaultChecked={vignetType.ProductID === mooringType}
              type="radio"
              name="vignetType"
              value={vignetType.ProductID}
              onChange={handleOnChange}
            />
            <div>{vignetType.Description}</div>
          </div>
        ))
      ) : (
        <div>Loading vignet types..</div>
      )}
    </>
    <div id={style.pageError}>{pageError}</div>
  </>
);

const Step2ContactInfo = ({ formData, validators, pageError }: any) => (
  <>
    <div id={style.header}>Contactgegevens</div>
    <div id={style.subHeader}>
      Vul hier uw contactgegevens in, zodat u een bevestiging ontvangt.
    </div>

    <LabeledInputField
      text={'Voorletters'}
      name={'firstName'}
      formData={formData}
      validator={validators.required}
    />
    <LabeledInputField text={'Tussenvoegsel'} name={'prefix'} formData={formData} />
    <LabeledInputField
      text={'Achternaam'}
      name={'lastName'}
      formData={formData}
      validator={validators.required}
    />
    <LabeledInputField
      text={'Telefoonnummer'}
      name={'phoneNumber'}
      formData={formData}
      validator={validators.isValidPhoneNumber}
    />
    <LabeledInputField
      text={'Herhaal telefoonnummer'}
      name={'repeatPhoneNumber'}
      formData={formData}
      validator={validators.isSamePhoneNumber}
    />
    <LabeledInputField
      text={'E-mailadres'}
      name={'email'}
      formData={formData}
      validator={validators.isValidEmail}
    />
    <LabeledInputField
      text={'Herhaal e-mailadres'}
      name={'repeatEmail'}
      formData={formData}
      validator={validators.isSameEmail}
    />

    <h6>Velden met * zijn verplicht.</h6>
    <div id={style.pageError}>{pageError}</div>
  </>
);

const Step3Payment = ({
  paymentType,
  paymentTypes,
  cardServiceCode,
  onChangePayment,
  onChangeService,
  valid,
  pageError,
}: any) => {
  const validityStyling = !valid && !paymentType ? style.invalid : style.valid;
  return (
    <>
      <div id={style.header}>Betalen</div>
      <div id={style.subHeader}>Kies betaalmethode.</div>
      <PaymentTypeSelector
        serviceCode={cardServiceCode}
        paymentType={paymentType}
        paymentTypes={paymentTypes}
        onChangePaymentType={onChangePayment}
        onChangeServiceCode={onChangeService}
        validityStyling={validityStyling}
      />
      <div id={style.pageError}>{pageError}</div>
    </>
  );
};

const Step4Summary = ({
  vignetTypeObject,
  formData,
  paymentType,
  truthfulAgreement,
  setTruthfulAgreement,
  endUserAgreement,
  setEndUserAgreement,
  valid,
  pageError,
  staticMode,
  onEditStep,
  paymentStatusDOM,
}: any) => (
  <>
    <div id={style.header}>Samenvatting</div>
    <div id={style.subHeader}>Hieronder staan uw gegevens voor afmeren.</div>

    <KeyValuePair horizontal={true} label={'Duur afmeren'} value={vignetTypeObject?.Name || ''} />

    <EditHeader text={'Contactgegevens'} onEdit={() => onEditStep(2)} hideEdit={staticMode} />
    <KeyValuePair horizontal={true} label={'Voorletters'} value={formData.firstName?.value || ''} />
    <KeyValuePair horizontal={true} label={'Tussenvoegsel'} value={formData?.prefix?.value || ''} />
    <KeyValuePair horizontal={true} label={'Achternaam'} value={formData.lastName?.value || ''} />
    <KeyValuePair
      horizontal={true}
      label={'Telefoonnummer'}
      value={formData.phoneNumber?.value || ''}
    />
    <KeyValuePair horizontal={true} label={'E-mailadres'} value={formData.email?.value || ''} />

    <EditHeader text={'Betaling'} onEdit={() => onEditStep(3)} hideEdit={staticMode} />
    <KeyValuePair
      horizontal={true}
      label={'Betaalwijze'}
      value={paymentType?.split(':')[0] || ''}
    />
    <br />
    <KeyValuePair horizontal={true} label={'Kosten'} value={vignetTypeObject?.Price || ''} />
    <KeyValuePair horizontal={true} label={'Totaalbedrag'} value={vignetTypeObject?.Price || ''} />

    <AgreementBox
      defaultChecked={truthfulAgreement}
      validity={valid || truthfulAgreement}
      text={
        'Ik heb alle gegevens correct en volledig ingevuld. Ik begrijp dat een aanvraag via internet dezelfde juridische status heeft als een aanvraag met een geschreven geldige handtekening.'
      }
      onChange={setTruthfulAgreement}
    />
    <AgreementBox
      defaultChecked={endUserAgreement}
      validity={valid || endUserAgreement}
      text={
        'Ik ben op de hoogte van de regels die op het water van Amsterdam gelden, zoals ze staan beschreven in de Binnenhavengeldverordening Pleziervaart en de Verordening op het binnenwater 2010. Kijk op '
      }
      onChange={setEndUserAgreement}
    >
      <a
        href="https://www.amsterdam.nl/parkeren-verkeer/varen-amsterdam/re-geling-verordeningen-water/"
        target="_blank"
        rel="noreferrer"
      >
        "https://www.amsterdam.nl/parkeren-verkeer/varen-amsterdam/re-geling-verordeningen-water/"{' '}
      </a>
      <div>voor meer informatie.</div>
    </AgreementBox>

    <div id={style.pageError}>{pageError}</div>
    {/* Wrapped in a fragment to fix the TS boolean/null error */}
    <>{staticMode && paymentStatusDOM}</>
  </>
);

// --- MAIN COMPONENT ---

const TransitPage = () => {
  const navigate = useNavigate();
  const params = useParams();

  const [action, setAction] = React.useState<number>(0);
  const [mooringType, setMooringType] = React.useState<string | undefined>(undefined);
  const [paymentType, setPaymentType] = React.useState<string | undefined>(undefined);
  const [formData, setFormData] = React.useState<TFormData>({});
  const [cardServiceCode, setCardServiceCode] = React.useState<string | undefined>();
  const [progress, setProgress] = React.useState<number>(0);
  const [pageError, setPageError] = React.useState<string | undefined>(undefined);
  const [valid, setValid] = React.useState<boolean>(true);
  const [truthfulAgreement, setTruthfulAgreement] = React.useState<boolean>(false);
  const [endUserAgreement, setEndUserAgreement] = React.useState<boolean>(false);
  const [staticMode, setStaticMode] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [mooringTypes, setMooringTypes] = React.useState<TPaymentType[] | undefined>(undefined);
  const [paymentTypes, setPaymentTypes] = React.useState<TPaymentType[] | undefined>(undefined);
  const [vignetTypes, setVignetTypes] = React.useState<TProductType[] | undefined>(undefined);

  React.useEffect(() => {
    DataApi.getMooringPaymentTypes(setMooringTypes, (e: Error) => setPageError(e.message));
    DataApi.getMooringPaymentTypes(setPaymentTypes, (e: Error) => setPageError(e.message));
    DataApi.getMooringTypes(setVignetTypes, (e: Error) => setPageError(e.message));
  }, []);

  // Form Validators
  const validators = {
    required: (value: string) => {
      setAction(action + 1);
      return value && value.trim().length > 0;
    },
    isValidPhoneNumber: () => {
      setAction(action + 1);
      return /^\+??\d{3}?[-\s\.]?\d{3}[-\s\.]?\d{4,9}$/im.test(formData.phoneNumber?.value || '');
    },
    isSamePhoneNumber: () => {
      setAction(action + 1);
      if (!formData.repeatPhoneNumber?.value) return false;
      const isSame = formData.phoneNumber.value === formData.repeatPhoneNumber.value;
      if (isSame) setValid(true);
      return isSame;
    },
    isValidEmail: () => {
      setAction(action + 1);
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email?.value || '');
    },
    isSameEmail: () => {
      setAction(action + 1);
      if (!formData.repeatEmail?.value) return false;
      const isSame = formData.email.value === formData.repeatEmail.value;
      if (isSame) setValid(true);
      return isSame;
    },
  };

  React.useEffect(() => {
    if (params.data && !formData.lastName) {
      try {
        const data = JSON.parse(window.atob(params.data));
        const newFormData: TFormData = {};

        for (const [key, value] of Object.entries(data)) {
          if (key === 'productID') setMooringType(value as string);
          else if (key === 'paymentTypeID' || key === 'vignetID') setPaymentType(value as string);
          else if (key === 'cardServiceCode') setCardServiceCode(value as string);
          else {
            if (key === 'email')
              newFormData.repeatEmail = {
                value: value as string,
                validator: validators.isSameEmail,
              };
            if (key === 'phoneNumber')
              newFormData.repeatPhoneNumber = {
                value: value as string,
                validator: validators.isSamePhoneNumber,
              };
            newFormData[key] = { value: value as string };
          }
        }
        setFormData(newFormData);
        setProgress(4);
        setStaticMode(true);
      } catch (e) {
        console.error('Failed to parse params data', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.data]);

  const steps: TStep[] = [
    { text: 'Nummer vignet invoeren' },
    { text: 'Kies 6 of 24 uur afmeren' },
    { text: 'Contactgegevens' },
    { text: 'Betalen' },
    { text: 'Samenvatting' },
    { text: 'Bedankt' },
  ];

  const handleOnValidateVignet = async (): Promise<boolean> => {
    if (!formData.vignetNumber?.value) {
      setPageError('Vul uw vignetnummer in.');
      return false;
    }
    if (!formData.repeatVignetNumber?.value) {
      setPageError('Vul nogmaals uw vignetnummer in.');
      return false;
    }
    if (formData.vignetNumber.value !== formData.repeatVignetNumber.value) {
      setPageError('Uw vignetnummers komen niet overeen.');
      return false;
    }

    setIsLoading(true);

    return new Promise((resolve) => {
      VignetApi.validateMooringVignet(
        formData.vignetNumber.value,
        () => {
          setIsLoading(false);
          setPageError(undefined);
          resolve(true);
        },
        (error: Error) => {
          setIsLoading(false);
          setPageError(error.message);
          resolve(false);
        },
      );
    });
  };

  const handleOnNext = async () => {
    setValid(true);
    setPageError(undefined);

    switch (progress) {
      case 0: {
        const isValid = await handleOnValidateVignet();
        if (!isValid) return;
        break;
      }
      case 1:
        if (!mooringType) {
          setValid(false);
          return setPageError('Kies één van bovenstaande opties!');
        }
        break;
      case 2:
        let isValidData = true;
        for (const [key, value] of Object.entries(formData)) {
          const entry = value as unknown as TFormDataEntry;
          if (entry.validator && !entry.validator()) isValidData = false;
        }
        if (!isValidData) {
          setValid(false);
          return setPageError('U heeft één of meerdere van de velden niet correct ingevuld');
        }
        break;
      case 3:
        if (!paymentType) {
          setValid(false);
          return setPageError('Kies één van bovenstaande opties!');
        }
        const isIdeal = paymentType.split(':')[0].toLowerCase() === 'ideal';
        if (!isIdeal && !cardServiceCode) {
          setValid(false);
          return setPageError('Kies voor Visa of Mastercard!');
        }
        break;
      case 4:
        if (!truthfulAgreement || !endUserAgreement) {
          setValid(false);
          return setPageError('Ga akkoord met het bovenstaande om verder te gaan.');
        }

        sendMooringRequest();
        return;
    }

    setProgress((prev) => prev + 1);
  };

  const handleOnPrevious = () => {
    setPageError(undefined);
    if (progress <= 0) return navigate('/');
    setProgress((prev) => prev - 1);
  };

  const sendMooringRequest = () => {
    setIsLoading(true);
    VignetApi.sendMooringRequest(
      formData.firstName?.value || '',
      formData?.prefix?.value || '',
      formData.lastName?.value || '',
      formData.vignetNumber?.value || '',
      formData.phoneNumber?.value || '',
      formData.email?.value || '',
      paymentType || '',
      cardServiceCode || '',
      mooringType || '',
      (json: any) => {
        setIsLoading(false);
        window.localStorage.setItem('access_token', json.access_token);
        window.location = json.redirect_url;
      },
      (error: Error) => {
        setIsLoading(false);
        setPageError(error.message);
      },
    );
  };

  const getPaymentStatusDOM = () => {
    switch (params.status) {
      case 'cancel':
      case 'reject':
      case 'error':
        return (
          <AlertBox
            severity={ESeverity.HIGH}
            header={'De betaling is mislukt.'}
            text={'Probeer het opnieuw of kies een andere manier om te betalen.'}
          />
        );
      case 'success':
        return <AlertBox severity={ESeverity.LOW} header={'De betaling is gelukt.'} text={''} />;
      default:
        return null;
    }
  };

  if (!mooringTypes) return <div>Loading payment methods.. {pageError}</div>;

  // Dynamically configure CenteredForm buttons based on state
  const getOnPreviousHandler = () => {
    if (progress === 0) return undefined;
    if (progress === 4 && staticMode) return undefined;
    return handleOnPrevious;
  };

  // --- RENDER DYNAMIC STEP ---
  const renderCurrentStep = () => {
    switch (progress) {
      case 0:
        return <Step0VignetNumber formData={formData} pageError={pageError} />;
      case 1:
        return (
          <Step1MooringType
            vignetTypes={vignetTypes}
            mooringType={mooringType}
            handleOnChange={(e: any) => setMooringType(e.target.value)}
            pageError={pageError}
          />
        );
      case 2:
        return (
          <Step2ContactInfo formData={formData} validators={validators} pageError={pageError} />
        );
      case 3:
        return (
          <Step3Payment
            paymentType={paymentType}
            paymentTypes={paymentTypes}
            cardServiceCode={cardServiceCode}
            onChangePayment={(e: any) => setPaymentType(e.target.value)}
            onChangeService={(e: any) => setCardServiceCode(e.target.value)}
            valid={valid}
            pageError={pageError}
          />
        );
      case 4:
        return (
          <Step4Summary
            vignetTypeObject={vignetTypes?.find((e) => e.ProductID === mooringType)}
            formData={formData}
            paymentType={paymentType}
            truthfulAgreement={truthfulAgreement}
            setTruthfulAgreement={setTruthfulAgreement}
            endUserAgreement={endUserAgreement}
            setEndUserAgreement={setEndUserAgreement}
            valid={valid}
            pageError={pageError}
            staticMode={staticMode}
            onEditStep={setProgress}
            paymentStatusDOM={getPaymentStatusDOM()}
          />
        );
      default:
        return null;
    }
  };

  return (
    <React.Fragment>
      <PageHeader className={style.pageHeader}></PageHeader>
      <div id={style.container}>
        <FormStepper steps={steps} currentStep={progress}></FormStepper>

        {/* We now only need ONE CenteredForm wrapper! */}
        <CenteredForm onPrevious={getOnPreviousHandler()} onNext={handleOnNext} locked={isLoading}>
          <TitleLineComponent text={'6 of 24 uur afmeren kopen'} />

          {renderCurrentStep()}
        </CenteredForm>
      </div>
    </React.Fragment>
  );
};

export default TransitPage;
